/**
 * @function errorLogger
 * @description Logs an error message along with optional additional parameters.
 * @param {Error|string} error - The error to be logged. It can be an Error object or a string message.
 * @param {Record<string, any>} params - Optional additional parameters to be logged along with the error.
 * @param {string} logType - Optional log type. It can be "error" or "warning". Default is "error".
 * @returns {void}
 */
export const errorLogger = (error: Error | string, params?: Record<string, any>, logType: "error" | "warning" = "error"): void => {
	const defaultMessage = "An error occurred: ";

	const logSingleError = (err: Error | string, type: "error" | "warning"): void => {
		if (type === "warning") {
			console.warn(defaultMessage, err);
		} else {
			console.error(defaultMessage, err);
		}
	};

	const logParamsError = (err: Error | string, parameters: string, type: "error" | "warning"): void => {
		if (type === "warning") {
			console.warn(defaultMessage, parameters, err);
		} else {
			console.error(defaultMessage, parameters, err);
		}
	};

	if (params) {
		const parameters = JSON.stringify(params, null, 4);
		logParamsError(error, parameters, logType);
	} else {
		logSingleError(error, logType);
	}
};

/**
 * @function getError
 * @description Returns a valid error object with proper typing.
 * This allows to handle the fact that TypeScript enforces the "unknown" type on errors caught in a try-catch block.
 * The caller can now safelly (as for type safety) call the stack property for runtime context.
 * @param {unknown} error - The error as an unknown type.
 * @returns {Error} The actual error object or a new one created from the given param.
 */
export function getError(error: unknown): Error {
	if (error instanceof Error) {
		return error;
	}
	return new Error(String(error));
}
